import { useQuery } from '@apollo/client';
import { useAuth } from '../contexts/AuthContext';
import { GET_ALL_USER_SUBSCRIPTIONS } from '@shared/graphql/queries/subscriptionPackages';

interface SubscriptionAccessResult {
  hasAccess: boolean;
  isLoading: boolean;
  accessibleTracks: string[];
  activeSubscriptions: any[];
  isSubscribed: boolean;
}

/**
 * Hook to check if the current user has subscription access to a specific track
 * @param trackId - The track ID to check access for
 * @param meetingDate - Optional meeting date (YYYY-MM-DD) to validate access for specific date
 * @returns Object with access status and subscription data
 */
export const useSubscriptionAccess = (trackId?: string, meetingDate?: string): SubscriptionAccessResult => {
  const { isAuthenticated, user } = useAuth();
  
  // Query user subscription data including default packages
  const { data, loading, error } = useQuery(GET_ALL_USER_SUBSCRIPTIONS, {
    variables: {
      meetingDate
    },
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network', // Always check for latest subscription status
    errorPolicy: 'all' // Don't fail completely on errors
  });

  // If user is not authenticated, they don't have access
  if (!isAuthenticated) {
    return {
      hasAccess: false,
      isLoading: false,
      accessibleTracks: [],
      activeSubscriptions: [],
      isSubscribed: false
    };
  }

  // While loading subscription data
  if (loading) {
    return {
      hasAccess: false, // Assume no access while loading
      isLoading: true,
      accessibleTracks: [],
      activeSubscriptions: [],
      isSubscribed: false
    };
  }

  // Extract subscription data
  const subscriptionData = data?.getCurrentUserSubscription;
  const accessibleTracks = subscriptionData?.accessibleTracks || [];
  const activeSubscriptions = subscriptionData?.activeSubscriptions || [];
  // Only count paid (non-default/non-promo) subscriptions
  const isSubscribed = activeSubscriptions.some((sub: any) => !sub.isDefaultPackage);

  console.log('🔍 [SUBSCRIPTION ACCESS DEBUG] Data received:', {
    subscriptionData: subscriptionData ? 'present' : 'missing',
    accessibleTracks,
    activeSubscriptionsCount: activeSubscriptions.length,
    activeSubscriptions,
    isSubscribed,
    trackId,
    meetingDate
  });

  // Check if user has access to the specific track.
  // If no trackId is provided (e.g. non-track pages like Tippa's Picks),
  // access is granted only if the user has an active subscription.
  const hasAccess = trackId
    ? accessibleTracks.includes(trackId)
    : isSubscribed;

  return {
    hasAccess,
    isLoading: false,
    accessibleTracks,
    activeSubscriptions,
    isSubscribed
  };
};